import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  Users,
  QrCode,
  MapPin,
  Settings,
  BarChart3,
  Webhook,
  LogOut,
  Menu,
  X,
  UserCog,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/check-in", icon: QrCode, label: "Check-In" },
    { to: "/members", icon: Users, label: "Members" },
  ];

  const adminNavItems = [
    { to: "/locations", icon: MapPin, label: "Locations", roles: ["super_admin", "main_office", "location_admin"] },
    { to: "/users", icon: UserCog, label: "Users", roles: ["super_admin", "main_office", "location_admin"] },
    { to: "/webhook-logs", icon: Webhook, label: "Webhook Logs", roles: ["super_admin", "location_admin"] },
  ];

  const bottomNavItems = [
    { to: "/reports", icon: BarChart3, label: "Reports" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ];

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: "Super Admin", color: "bg-blue-100 text-blue-800" },
      main_office: { label: "Main Office", color: "bg-indigo-100 text-indigo-800" },
      location_admin: { label: "Location Admin", color: "bg-purple-100 text-purple-800" },
      staff: { label: "Staff", color: "bg-gray-100 text-gray-800" }
    };
    return badges[role] || badges.staff;
  };

  const renderNavItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `nav-item ${isActive ? "active" : ""}`
      }
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <item.icon size={20} />
      <span>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="flex min-h-screen">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar fixed md:sticky top-0 z-50 ${
          sidebarOpen ? "" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <QrCode className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900 text-lg">STP</h1>
                <p className="text-xs text-gray-500">Membership</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
              data-testid="close-sidebar-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            <div className="space-y-1">
              {navItems.map(renderNavItem)}
            </div>

            {/* Admin section */}
            {(user?.role === "super_admin" || user?.role === "main_office" || user?.role === "location_admin") && (
              <div className="pt-6">
                <p className="px-3 mb-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Administration
                </p>
                <div className="space-y-1">
                  {adminNavItems
                    .filter((item) => !item.roles || item.roles.includes(user?.role))
                    .map(renderNavItem)}
                </div>
              </div>
            )}

            {/* Bottom section */}
            <div className="pt-6">
              <p className="px-3 mb-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                Tools
              </p>
              <div className="space-y-1">
                {bottomNavItems.map(renderNavItem)}
              </div>
            </div>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-white text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name}
                </p>
                <span className={`inline-flex text-xs px-2 py-0.5 rounded-full ${getRoleBadge(user?.role).color}`}>
                  {getRoleBadge(user?.role).label}
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="header h-16 flex items-center justify-between px-4 md:px-6" data-testid="header">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            data-testid="open-sidebar-btn"
          >
            <Menu size={20} />
          </button>

          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-white text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                <ChevronRight className="w-4 h-4 rotate-90" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
